import React, { useState } from 'react';
import styled from 'styled-components';
import useTranslation from '@src/hooks/useTranslation';

import useMediaQuery from '@src/hooks/useMediaQuery';
import useEvent from '@src/hooks/useEvent';
import { OFFLINE_EVENT_DROPDOWN_ANCHOR, DEFAULT_SLOT_TYPE } from '@src/const/event';

import Modal from '@src/components/common/modal';
import SaleCountdown from './ticketSelection/saleCountdown';
import PriceButton from './priceButton';
import TicketSelection from './ticketSelection';

const FixedPriceButtonWrapper = styled.div`
	width: 100vw;
	position: fixed;
	bottom: 0;
	left: 0;
	padding: 12px;
	background-color: #ffffff;
	box-shadow: 0px 0px 8px rgba(0, 0, 0, 0.15);
	z-index: 999;

	@media ${props => props.theme.device.lgDesktop} {
		display: none;
	}

	@media ${props => props.theme.device.desktop} {
		display: none;
	}

	@media ${props => props.theme.device.tablet} {
		display: none;
	}
`;

const genModalFooter = ({ slotType, ticketType, buttonText, handleAddEventToCart }) => (
	<PriceButton
		slotType={slotType}
		ticketType={ticketType}
		buttonText={buttonText}
		handleButtonClick={handleAddEventToCart}
	/>
);

interface IFixedPriceButton {
	className?: string;
	showCountdown: boolean;
	timeForCountdown: string;
	showSoldOut: boolean;
	slotType: string;
	ticketType: string;
	ticketNumber: number;
	canPurchase: boolean;
	dropdownError: {
		slotType: boolean;
		ticketType: boolean;
		ticketNumber: boolean;
	};
	handleSelectSlot: (type: string) => void;
	handleSelectTicket: (type: string) => void;
	handleSelectTicketNumber: (type: number) => void;
	handleAddEventToCart: () => void;
	resetDropdownError: () => void;
}

const FixedPriceButton = ({
	className, showCountdown, timeForCountdown, showSoldOut, slotType, ticketType, ticketNumber, canPurchase,
	dropdownError, handleSelectSlot, handleSelectTicket, handleSelectTicketNumber, handleAddEventToCart,
	resetDropdownError,
}: IFixedPriceButton): JSX.Element => {
	const { t } = useTranslation();
	const [isModalActive, setIsModalActive] = useState(false);
	const { isSignedIn, handleLogin } = useEvent();
	const { mediaMatch: { isMobile } } = useMediaQuery();

	const modalFooter = genModalFooter({
		slotType,
		ticketType,
		buttonText: t('event:add_to_cart'),
		handleAddEventToCart: () => {
			if (canPurchase) {
				handleCloseModal();
			}

			handleAddEventToCart();
		},
	});

	const handleClickScroll = () => {
		const targetEle = document.getElementById(OFFLINE_EVENT_DROPDOWN_ANCHOR);

		window.scrollTo({
			top: targetEle.offsetTop - 20,
			behavior: 'smooth',
		});
	};
	const handleSelectSlotButton = () => {
		if (isSignedIn) {
			if (isMobile) {
				handleSelectSlot(DEFAULT_SLOT_TYPE);
				setIsModalActive(true);
			} else {
				handleClickScroll();
			}
		} else {
			handleLogin();
		}
	};
	const handleCloseModal = () => {
		handleSelectSlot(DEFAULT_SLOT_TYPE);
		setIsModalActive(false);
		resetDropdownError();
	};

	return (
		<FixedPriceButtonWrapper className={className}>
			{showCountdown ? (
				<SaleCountdown time={timeForCountdown} fontSize={{ numberSize: 24, unitSize: 14 }} fontWeight={400} />
			) : (
				<PriceButton
					slotType={slotType}
					ticketType={ticketType}
					buttonText={showSoldOut ? t('event:sold_out_tag') : t('event:select_slot')}
					isDisable={showSoldOut}
					handleButtonClick={handleSelectSlotButton}
				/>
			)}
			{isMobile && isModalActive && (
				<Modal
					isActive={isModalActive}
					headerText={t('event:select_slot')}
					footer={modalFooter}
					handleClose={handleCloseModal}
				>
					<TicketSelection
						showCountdown={showCountdown}
						timeForCountdown={timeForCountdown}
						showSoldOut={showSoldOut}
						enablePurchase
						slotType={slotType}
						ticketType={ticketType}
						ticketNumber={ticketNumber}
						dropdownError={dropdownError}
						handleSelectSlot={handleSelectSlot}
						handleSelectTicket={handleSelectTicket}
						handleSelectTicketNumber={handleSelectTicketNumber}
					/>
				</Modal>
			)}
		</FixedPriceButtonWrapper>
	);
};

export default FixedPriceButton;
